import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Eye, ShieldCheck, XCircle } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { kyc } from "@/api/endpoints";
import type { VerificationStatus } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { Field, FilterBar, PageHeader, Pagination } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EmptyState,
  ErrorState,
  FormError,
  Spinner,
  TableSkeleton,
} from "@/components/ui/feedback";
import { Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDate, formatDateTime, humanize } from "@/lib/utils";

const STATUS_OPTIONS = [
  { label: "Pending review", value: "PENDING_REVIEW" },
  { label: "Pending", value: "PENDING" },
  { label: "Verified", value: "VERIFIED" },
  { label: "Rejected", value: "REJECTED" },
];

export default function Kyc() {
  const [page, setPage] = React.useState(1);
  const [status, setStatus] = React.useState("");
  const [openId, setOpenId] = React.useState<string | null>(null);

  React.useEffect(() => setPage(1), [status]);

  const query = useQuery({
    queryKey: ["kyc", "queue", { page, status }],
    queryFn: () =>
      kyc.queue({
        page,
        page_size: 20,
        verification_status: (status || undefined) as VerificationStatus | undefined,
      }),
  });

  return (
    <>
      <PageHeader
        title="KYC review"
        description="Providers waiting on identity verification. They cannot accept jobs until reviewed."
      />

      <FilterBar>
        <div className="w-52">
          <Select
            value={status}
            onValueChange={setStatus}
            options={STATUS_OPTIONS}
            placeholder="Awaiting review"
            aria-label="Filter by verification status"
          />
        </div>
        <p className="ml-auto text-xs text-muted-foreground">
          Opening a record is logged against your account.
        </p>
      </FilterBar>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={5} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data && query.data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applied</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.business_name}</TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {p.phone ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={toneFor(p.verification_status)}>
                        {humanize(p.verification_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {formatDate(p.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setOpenId(p.id)}>
                        <Eye />
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={query.data.page}
              pageSize={query.data.pageSize}
              total={query.data.total}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={ShieldCheck}
            title="Review queue is clear"
            description="No providers are currently waiting on verification."
          />
        )}
      </Card>

      {openId && <ReviewDrawer providerId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function ReviewDrawer({
  providerId,
  onClose,
}: {
  providerId: string;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  // Fetching this is itself an audited event on the server — one read per open.
  const detail = useQuery({
    queryKey: ["kyc", "detail", providerId],
    queryFn: () => kyc.detail(providerId),
  });

  const review = useMutation({
    mutationFn: ({ approve }: { approve: boolean }) =>
      kyc.review(providerId, approve, reason || undefined),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.approve ? "Provider verified" : "Provider rejected",
        detail.data?.provider.business_name,
      );
      void queryClient.invalidateQueries({ queryKey: ["kyc"] });
      void queryClient.invalidateQueries({ queryKey: ["providers"] });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not submit the review."),
  });

  function submit(approve: boolean) {
    setError(null);
    // Mirrors the server rule so the reviewer finds out before the round trip.
    if (!approve && !reason.trim()) {
      setError("A rejection reason is required — the provider sees this text.");
      return;
    }
    review.mutate({ approve });
  }

  const identity = detail.data?.identity;
  const provider = detail.data?.provider;
  const canReview = can("kyc.review");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent side="right">
        <DialogHeader>
          <DialogTitle>{provider?.business_name ?? "Loading…"}</DialogTitle>
          <DialogDescription>
            Identity data is shown masked. Your access to this record has been logged.
          </DialogDescription>
        </DialogHeader>

        {detail.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-5 text-muted-foreground" />
          </div>
        ) : detail.isError ? (
          <ErrorState error={detail.error} onRetry={() => detail.refetch()} />
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Status">
                <Badge variant={toneFor(provider?.verification_status)}>
                  {humanize(provider?.verification_status)}
                </Badge>
              </Field>
              <Field label="Phone">
                <span className="tabular">{provider?.phone ?? "—"}</span>
              </Field>
              <Field label="Location">
                {[provider?.city, provider?.state].filter(Boolean).join(", ") || "—"}
              </Field>
              <Field label="Applied">{formatDate(provider?.created_at)}</Field>
            </dl>

            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              DigiLocker identity
            </h3>
            <dl className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-muted/40 p-4">
              <Field label="Name on record">{identity?.digilocker_name ?? "—"}</Field>
              <Field label="Date of birth">{identity?.digilocker_dob ?? "—"}</Field>
              <Field label="Gender">
                {humanize(identity?.digilocker_gender) ?? "—"}
              </Field>
              <Field label="Aadhaar (masked)">
                <span className="tabular">
                  {identity?.digilocker_masked_aadhaar ?? "—"}
                </span>
              </Field>
              <Field label="Consent given">
                {formatDateTime(identity?.consented_at)}
              </Field>
              <Field label="DigiLocker verified">
                {formatDateTime(identity?.digilocker_verified_at)}
              </Field>
            </dl>

            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Verification attempts
            </h3>
            {detail.data?.attempts.length ? (
              <ul className="space-y-2">
                {detail.data.attempts.map((a) => (
                  <li
                    key={a.id}
                    className="rounded-md border border-border p-3 text-xs"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={toneFor(a.status)}>{humanize(a.status)}</Badge>
                      <span className="text-muted-foreground">
                        {humanize(a.provider_type)}
                      </span>
                      <span className="ml-auto text-muted-foreground">
                        {formatDateTime(a.created_at)}
                      </span>
                    </div>
                    {a.verified_name && (
                      <p className="mt-1.5">
                        Verified as{" "}
                        <span className="font-medium">{a.verified_name}</span>
                        {a.masked_identifier && (
                          <span className="tabular text-muted-foreground">
                            {" "}
                            · {a.masked_identifier}
                          </span>
                        )}
                      </p>
                    )}
                    {a.failure_reason && (
                      <p
                        className="mt-1.5"
                        style={{ color: "var(--status-critical)" }}
                      >
                        {a.failure_reason}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">
                No verification attempts recorded.
              </p>
            )}

            {canReview && (
              <div className="mt-6 space-y-2">
                <Label htmlFor="kyc-reason">
                  Reason{" "}
                  <span className="text-muted-foreground">
                    (required to reject — the provider sees this)
                  </span>
                </Label>
                <Textarea
                  id="kyc-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Name on Aadhaar does not match the registered business owner"
                />
                <FormError message={error} />
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {canReview && (
            <>
              <Button
                variant="destructive"
                disabled={review.isPending || detail.isLoading}
                onClick={() => submit(false)}
              >
                <XCircle />
                Reject
              </Button>
              <Button
                disabled={review.isPending || detail.isLoading}
                onClick={() => submit(true)}
              >
                {review.isPending ? <Spinner /> : <CheckCircle2 />}
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
